import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { EnvironmentService } from 'src/app/shared/common/environment/environment.service';
import { MapWorkspace } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace';
import { Actor } from 'src/app/shared/user/actor';
import { UserDTO } from 'src/app/shared/user/user';

import { HttpErrorCodes } from 'src/app/core/error-page/error-page.component';
import { PermissionType } from '../../map-data-services/mapWorkspace/permission';
import {
    GeoDataUpdateJob,
    GeoDataUpdateJobAssignee,
    GeoDataUpdateJobs,
    GeoPostDataUpdateJobRequest,
    Task,
    TaskPanelTabs,
    TaskPatchDTO
} from './task';

@Injectable({
    providedIn: 'root'
})
export class TaskService {
    constructor(private http: HttpClient, private env: EnvironmentService) {}

    async getTasksBySearch(
        projectId: string,
        workspaceId: string,
        status: TaskPanelTabs,
        searchInput: string = ''
    ): Promise<Task[]> {
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/gisjobs/_query?' + 'sortBy=UpdatedUtc DESC&';
        let statusQuery: string;

        const headers = new HttpHeaders().set('Range', 'items=0-100');

        if (status === TaskPanelTabs.CLOSED) {
            statusQuery = `eq 'Closed'`;
        } else if (status === TaskPanelTabs.ACTIVE) {
            statusQuery = `ne 'Closed'`;
        }

        let filter = `$WorkspaceId eq '${workspaceId}' AND $Status ${statusQuery}`;

        if (searchInput) {
            filter += ` AND $Name cn '${searchInput}'`;
        }

        taskPath += `filter=${encodeURIComponent(filter)}`;

        const taskResponse = await lastValueFrom(this.http.get<GeoDataUpdateJobs>(taskPath, { headers }));
        return taskResponse.items.map(item => Task.fromDTO(item));
    }

    async getTaskById(projectId: string, id: string): Promise<Task> {
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/gisjobs/' + id;

        const taskResponse = await lastValueFrom(this.http.get<GeoDataUpdateJob>(taskPath));

        let tmpTask = Task.fromDTO(taskResponse);
        return await this.checkTaskHasReturnJobUsers(projectId, tmpTask);
    }

    async checkTaskHasReturnJobUsers(projectId: string, tmpTask: Task): Promise<Task> {
        if (tmpTask.isTodoCompletedByUnassignedUser) {
            let promises: Promise<Partial<UserDTO>>[] = [];
            tmpTask.returnedJobs.forEach(job => {
                let path = this.env.apiUrl + '/projects/' + projectId + '/memberships';
                promises.push(lastValueFrom(this.http.get(path, { params: { userId: job.userId } })));
            });
            const responses = await Promise.all(promises);
            responses.forEach(response => {
                const tmpActor = Actor.fromDTO(response, PermissionType.USER);
                tmpActor.isTodoCompleted = true;
                tmpTask.unassignedCompletedUser.push(tmpActor);
            });
            return tmpTask;
        }
        return Promise.resolve(tmpTask);
    }

    async createTask(projectId: string, workspace: MapWorkspace, task: Task): Promise<Task> {
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/gisjobs';
        let taskResponse: GeoDataUpdateJob;

        let taskData: GeoPostDataUpdateJobRequest = {
            name: task.title,
            description: task.description,
            forms: task.features.map(taskFeature => ({
                formId: taskFeature.featureId,
                templateSeriesId: taskFeature.templateSeriesId,
                status: taskFeature.status
            })),
            assignees: task.assignees.map(assignee => ({
                type: assignee.type,
                id: assignee.id,
                tidUuid: assignee.tiduuid
            })) as GeoDataUpdateJobAssignee[],
            workspaceId: workspace.id
        };

        taskResponse = await lastValueFrom(this.http.post<GeoDataUpdateJob>(taskPath, taskData));

        return Task.fromDTO(taskResponse);
    }

    async updateTask(projectId: string, task: Task, editedProperties: TaskPatchDTO): Promise<Task> {
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/gisjobs';

        const headers = new HttpHeaders({
            'If-Match': `"${task.etag}"`
        });

        const options = { headers };

        try {
            const taskResponse = await lastValueFrom(
                this.http.patch<GeoDataUpdateJob>(taskPath + '/' + task.id, editedProperties, options)
            );
            return Task.fromDTO(taskResponse);
        } catch (error) {
            if (error.status === HttpErrorCodes.PRECONDITION_FAILED) {
                // If task version mismatch, get the latest task and try again
                const latestTask = await this.getTaskById(projectId, task.id);
                const latestHeaders = new HttpHeaders({
                    'If-Match': `"${latestTask.etag}"`
                });
                const latestOptions = { headers: latestHeaders };

                const latestTaskResponse = await lastValueFrom(
                    this.http.patch<GeoDataUpdateJob>(taskPath + '/' + latestTask.id, editedProperties, latestOptions)
                );
                return Task.fromDTO(latestTaskResponse);
            } else {
                throw error;
            }
        }
    }

    async deleteTask(projectId: string, task: Task): Promise<GeoDataUpdateJob> {
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/gisjobs';
        return await lastValueFrom(this.http.delete<GeoDataUpdateJob>(taskPath + '/' + task.id));
    }
}
